import React, { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { RotateCw, Trash2, Move, Save, Plus, Eye, EyeOff, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
    BrainInJar, BrainFlower, EscapeTheHerd, WallOfThoughts, AbnormalBrainMark,
} from "@/components/AbnormalIllustrations";
import {
    StickerKeepLeft, StickerRocks, StickerSendIt, StickerSketchy, StickerMindBoars,
    StickerCoords, StickerTrailSign, StickerCompass, StickerStay, StickerPeak,
} from "@/components/FieldStickers";

/*  ─────────────────────────────────────────────────────────────
 *   POSTER MAP — user-owned artworks + Argentario field stickers
 *  ────────────────────────────────────────────────────────────── */
const POSTERS = {
    brainMohawk:  { component: AbnormalBrainMark, label: "Abnormal Brain Mohawk", group: "poster" },
    brainFlower:  { component: BrainFlower,        label: "Grow Different", group: "poster" },
    normalsUFO:   { component: EscapeTheHerd,      label: "Normals Get Left Behind", group: "poster" },
    wallThoughts: { component: WallOfThoughts,     label: "Wall of Thoughts", group: "poster" },
    brainLine:    { component: BrainInJar,         label: "Abnormal Brain (line)", group: "poster" },
    stKeepLeft:   { component: StickerKeepLeft,    label: "keep left", group: "sticker", size: 170 },
    stRocks:      { component: StickerRocks,       label: "rocks!", group: "sticker", size: 150 },
    stSendIt:     { component: StickerSendIt,      label: "send it", group: "sticker", size: 150 },
    stSketchy:    { component: StickerSketchy,     label: "sketchy", group: "sticker", size: 160 },
    stBoars:      { component: StickerMindBoars,   label: "mind the boars", group: "sticker", size: 200 },
    stCoords:     { component: StickerCoords,      label: "Timbro coordinate", group: "sticker", size: 180 },
    stTrailSign:  { component: StickerTrailSign,   label: "Segnale trail", group: "sticker", size: 140 },
    stCompass:    { component: StickerCompass,     label: "Bussola", group: "sticker", size: 90 },
    stStay:       { component: StickerStay,        label: "Timbro Stay Weird", group: "sticker", size: 110 },
    stPeak:       { component: StickerPeak,        label: "Punta Telegrafo", group: "sticker", size: 120 },
};
const GROUPS = [["poster", "// poster"], ["sticker", "// timbri & note di campo"]];

const uid = () => Math.random().toString(36).slice(2, 10);

/**
 * DecorationLayer
 *
 * Renders the admin-configurable overlay of Abnormal posters on top of
 * a page. Any visitor sees the posters. An admin sees an "Edit mode"
 * toolbar that lets them drag, resize, rotate, delete and add posters
 * with mouse/touch — changes persist via PUT /api/decorations/{page}.
 *
 * Positioning:
 *   • `top`  = px from top of the page container (page is `position: relative`)
 *   • `left` = percentage of container width (0..100)
 *
 * The parent page MUST wrap its content in a `relative` container for
 * absolute posters to anchor correctly.
 */
export default function DecorationLayer({ page }) {
    const { user } = useAuth();
    const isAdmin = user && user.role === "admin";

    const [items, setItems] = useState([]);
    const [loaded, setLoaded] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [pickerOpen, setPickerOpen] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const containerRef = useRef(null);

    /* ─── LOAD ─── */
    useEffect(() => {
        api.get(`/decorations/${page}`)
            .then((r) => setItems(r.data.items || []))
            .catch(() => setItems([]))
            .finally(() => setLoaded(true));
    }, [page]);

    /* ─── MUTATIONS ─── */
    const patchItem = useCallback((id, patch) => {
        setItems((cur) => cur.map((it) => (it.id === id ? { ...it, ...patch } : it)));
        setDirty(true);
    }, []);
    const removeItem = useCallback((id) => {
        setItems((cur) => cur.filter((it) => it.id !== id));
        setSelectedId(null);
        setDirty(true);
    }, []);
    const addItem = useCallback((poster) => {
        const newItem = {
            id: uid(), poster, top: 200, left: 50, size: POSTERS[poster]?.size || 180, tilt: 0, zIndex: 20,
        };
        setItems((cur) => [...cur, newItem]);
        setSelectedId(newItem.id);
        setPickerOpen(false);
        setDirty(true);
    }, []);

    const save = useCallback(async () => {
        setSaving(true);
        try {
            await api.put(`/decorations/${page}`, { items });
            setDirty(false);
            toast.success("Decorazioni salvate");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Errore nel salvare");
        } finally {
            setSaving(false);
        }
    }, [page, items]);

    /* ─── DRAG (mouse + touch) ─── */
    const dragRef = useRef(null);
    const onPointerDownItem = (e, id) => {
        if (!editMode) return;
        e.stopPropagation();
        setSelectedId(id);
        const item = items.find((i) => i.id === id);
        if (!item) return;
        const containerRect = containerRef.current.getBoundingClientRect();
        dragRef.current = {
            id,
            startX: e.clientX,
            startY: e.clientY,
            startTop: item.top,
            startLeft: item.left,
            containerWidth: containerRect.width,
        };
        window.addEventListener("pointermove", onPointerMove);
        window.addEventListener("pointerup", onPointerUp, { once: true });
    };
    const onPointerMove = (e) => {
        const d = dragRef.current;
        if (!d) return;
        const dx = e.clientX - d.startX;
        const dy = e.clientY - d.startY;
        const newLeft = Math.min(100, Math.max(0, d.startLeft + (dx / d.containerWidth) * 100));
        const newTop = Math.max(0, d.startTop + dy);
        setItems((cur) => cur.map((it) => (it.id === d.id ? { ...it, top: newTop, left: newLeft } : it)));
        setDirty(true);
    };
    const onPointerUp = () => {
        dragRef.current = null;
        window.removeEventListener("pointermove", onPointerMove);
    };

    if (!loaded) return null;

    return (
        <>
            {/* posters overlay */}
            <div
                ref={containerRef}
                className="absolute inset-0 pointer-events-none"
                style={{ zIndex: 5 }}
                aria-hidden={!editMode}
            >
                {items.map((it) => {
                    const P = POSTERS[it.poster]?.component;
                    if (!P) return null;
                    const isSelected = editMode && selectedId === it.id;
                    return (
                        <div
                            key={it.id}
                            onPointerDown={(e) => onPointerDownItem(e, it.id)}
                            onClick={(e) => { if (editMode) { e.stopPropagation(); setSelectedId(it.id); } }}
                            style={{
                                position: "absolute",
                                top: it.top,
                                left: `${it.left}%`,
                                transform: `translateX(-50%)`,
                                zIndex: it.zIndex || 20,
                                cursor: editMode ? "move" : "default",
                                pointerEvents: editMode ? "auto" : "none",
                                outline: isSelected ? "1.5px dashed #C9FF3D" : "none",
                                outlineOffset: 6,
                                touchAction: editMode ? "none" : "auto",
                            }}
                            data-testid={`deco-${it.id}`}
                        >
                            <P size={it.size} tilt={it.tilt} />
                            {isSelected && <ItemHandles item={it} onPatch={patchItem} onRemove={removeItem} />}
                        </div>
                    );
                })}
            </div>

            {/* Admin toolbar — only visible to admin */}
            {isAdmin && (
                <AdminToolbar
                    editMode={editMode}
                    setEditMode={setEditMode}
                    dirty={dirty}
                    saving={saving}
                    onSave={save}
                    onOpenPicker={() => setPickerOpen(true)}
                    itemCount={items.length}
                />
            )}
            {pickerOpen && <PosterPicker onPick={addItem} onClose={() => setPickerOpen(false)} />}
        </>
    );
}

/* ─── Selection handles (resize + rotate + delete) ─── */
function ItemHandles({ item, onPatch, onRemove }) {
    return (
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-center gap-2 bg-black border border-abnormal-acid px-2 py-1 z-30 whitespace-nowrap"
             onPointerDown={(e) => e.stopPropagation()}
             onClick={(e) => e.stopPropagation()}
        >
            <label className="font-vt text-[10px] text-abnormal-acid tracking-[0.2em] uppercase flex items-center gap-1">
                SIZE
                <input
                    type="range" min="60" max="600" value={item.size}
                    onChange={(e) => onPatch(item.id, { size: parseFloat(e.target.value) })}
                    className="w-24 accent-abnormal-acid"
                    data-testid={`deco-size-${item.id}`}
                />
                <span className="w-8 text-abnormal-paper">{Math.round(item.size)}</span>
            </label>
            <label className="font-vt text-[10px] text-abnormal-acid tracking-[0.2em] uppercase flex items-center gap-1">
                TILT
                <input
                    type="range" min="-45" max="45" value={item.tilt}
                    onChange={(e) => onPatch(item.id, { tilt: parseFloat(e.target.value) })}
                    className="w-20 accent-abnormal-acid"
                    data-testid={`deco-tilt-${item.id}`}
                />
                <span className="w-8 text-abnormal-paper">{Math.round(item.tilt)}°</span>
            </label>
            <button
                type="button"
                onClick={() => onPatch(item.id, { tilt: 0 })}
                className="text-abnormal-paperDim hover:text-abnormal-acid"
                title="Reset rotazione"
                data-testid={`deco-resetrot-${item.id}`}
            >
                <RotateCw size={12} />
            </button>
            <button
                type="button"
                onClick={() => onRemove(item.id)}
                className="text-abnormal-red hover:text-abnormal-acid"
                title="Elimina"
                data-testid={`deco-remove-${item.id}`}
            >
                <Trash2 size={13} />
            </button>
        </div>
    );
}

/* ─── Floating admin toolbar at bottom-right ─── */
function AdminToolbar({ editMode, setEditMode, dirty, saving, onSave, onOpenPicker, itemCount }) {
    return (
        <div
            className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-2 bg-black border border-abnormal-acid px-3 py-2 shadow-2xl"
            data-testid="deco-admin-toolbar"
        >
            <span className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-acid">
                [ DECORATIONS · {itemCount} ]
            </span>
            <button
                type="button"
                onClick={() => setEditMode(!editMode)}
                className={`font-vt text-[11px] uppercase tracking-[0.3em] px-2 py-1 border ${
                    editMode ? "bg-abnormal-acid text-black border-abnormal-acid" : "text-abnormal-paper border-white/20 hover:border-abnormal-acid"
                }`}
                data-testid="deco-editmode-toggle"
            >
                {editMode ? <><EyeOff size={11} className="inline mr-1" /> Esci</> : <><Eye size={11} className="inline mr-1" /> Modifica</>}
            </button>
            {editMode && (
                <>
                    <button
                        type="button"
                        onClick={onOpenPicker}
                        className="font-vt text-[11px] uppercase tracking-[0.3em] px-2 py-1 text-abnormal-paper border border-white/20 hover:border-abnormal-acid"
                        data-testid="deco-add"
                    >
                        <Plus size={11} className="inline mr-1" /> Aggiungi
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={!dirty || saving}
                        className="font-vt text-[11px] uppercase tracking-[0.3em] px-2 py-1 bg-abnormal-acid text-black hover:bg-abnormal-paper disabled:opacity-40"
                        data-testid="deco-save"
                    >
                        <Save size={11} className="inline mr-1" /> {saving ? "…" : "Salva"}{dirty ? " *" : ""}
                    </button>
                </>
            )}
        </div>
    );
}

/* ─── Poster picker modal ─── */
function PosterPicker({ onPick, onClose }) {
    return (
        <div className="fixed inset-0 z-[90] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-black border border-abnormal-acid p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex items-center justify-between mb-5">
                    <p className="font-vt text-xs tracking-[0.3em] uppercase text-abnormal-acid">// aggiungi al foglio</p>
                    <button onClick={onClose} className="text-abnormal-paper hover:text-abnormal-acid"><X size={18} /></button>
                </div>
                {GROUPS.map(([g, title]) => (
                    <div key={g} className="mb-6">
                        <p className="font-vt text-[10px] tracking-[0.3em] uppercase text-abnormal-paperDim/70 mb-3">{title}</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            {Object.entries(POSTERS).filter(([, m]) => m.group === g).map(([key, meta]) => {
                                const P = meta.component;
                                return (
                                    <button
                                        key={key}
                                        type="button"
                                        onClick={() => onPick(key)}
                                        className="group flex flex-col items-center gap-2 p-3 border border-white/10 hover:border-abnormal-acid transition-colors bg-black overflow-hidden"
                                        data-testid={`picker-${key}`}
                                    >
                                        <div className="h-20 flex items-center justify-center">
                                            <P size={g === "poster" ? 80 : 110} />
                                        </div>
                                        <p className="font-vt text-[10px] tracking-[0.25em] uppercase text-abnormal-paper group-hover:text-abnormal-acid">
                                            {meta.label}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
